import type { FmpCongressTradeRow, FmpCongressTradesFileResponse } from '@/services/api';
import {
  classifyCongressTrade,
  congressMemberName,
  congressTradeCategoryLabel,
  congressTradeChamber,
  congressTradeIdentityKey,
  fmpCongressTradeMarkerKey,
  tradeMatchesEnabledCongressCategories,
  tradeMatchesEnabledCongressChambers,
  type CongressChamber,
  type CongressTradeCategory,
} from '@/utils/congressTradingTypes';

export interface CongressTradeSearchResult {
  trade: FmpCongressTradeRow;
  transactionDate: string;
  memberName: string;
  category: CongressTradeCategory;
  chamber: CongressChamber | null;
  typeLabel: string;
  metricLabel: string;
  markerKey: string;
  tradeIdentityKey: string;
  onChart: boolean;
}

function tradeMatchesSearchQuery(trade: FmpCongressTradeRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  const member = congressMemberName(trade).toLowerCase();
  if (member.includes(q)) return true;
  const first = (trade.firstName ?? '').toLowerCase();
  const last = (trade.lastName ?? '').toLowerCase();
  if (first.includes(q) || last.includes(q)) return true;
  if (`${first} ${last}`.trim().includes(q)) return true;
  const district = (trade.district ?? '').toLowerCase();
  if (district.includes(q)) return true;
  const owner = (trade.owner ?? '').toLowerCase();
  if (owner.includes(q)) return true;
  const asset = (trade.assetDescription ?? '').toLowerCase();
  if (asset.includes(q)) return true;
  const type = (trade.type ?? '').toLowerCase();
  if (type.includes(q)) return true;
  const chamber = (trade.chamber ?? '').toLowerCase();
  if (chamber.includes(q)) return true;
  return false;
}

function formatAmountMetric(trade: FmpCongressTradeRow): string {
  const amount = (trade.amount ?? '').trim();
  return amount || '—';
}

export function tradeMatchesCongressLayerFilters(
  trade: FmpCongressTradeRow,
  enabledCategories: ReadonlySet<CongressTradeCategory>,
  enabledChambers: ReadonlySet<CongressChamber>,
  searchQuery: string
): boolean {
  if (!tradeMatchesEnabledCongressCategories(trade, enabledCategories)) return false;
  if (!tradeMatchesEnabledCongressChambers(trade, enabledChambers)) return false;
  const q = searchQuery.trim();
  if (q.length >= 2 && !tradeMatchesSearchQuery(trade, q)) return false;
  return true;
}

/**
 * Search congress filings; respects category/chamber filters and optional chart-date scope.
 */
export function searchCongressTrades(
  payload: FmpCongressTradesFileResponse | null | undefined,
  query: string,
  enabledCategories: ReadonlySet<CongressTradeCategory>,
  enabledChambers: ReadonlySet<CongressChamber>,
  chartDates: ReadonlySet<string>,
  maxResults = 48
): CongressTradeSearchResult[] {
  const q = query.trim();
  if (q.length < 2 || !payload?.congress_trades?.length) return [];

  const byDate = new Map<string, FmpCongressTradeRow[]>();
  for (const trade of payload.congress_trades) {
    if (!tradeMatchesSearchQuery(trade, q)) continue;
    if (!tradeMatchesEnabledCongressCategories(trade, enabledCategories)) continue;
    if (!tradeMatchesEnabledCongressChambers(trade, enabledChambers)) continue;
    const transactionDate = (trade.transactionDate ?? '').trim();
    if (!transactionDate) continue;
    const arr = byDate.get(transactionDate) ?? [];
    arr.push(trade);
    byDate.set(transactionDate, arr);
  }

  const out: CongressTradeSearchResult[] = [];
  const sortedDates = [...byDate.keys()].sort((a, b) => b.localeCompare(a));
  for (const transactionDate of sortedDates) {
    const dayTrades = byDate.get(transactionDate)!;
    dayTrades.forEach((trade, stackIndex) => {
      out.push({
        trade,
        transactionDate,
        memberName: congressMemberName(trade),
        category: classifyCongressTrade(trade),
        chamber: congressTradeChamber(trade),
        typeLabel: congressTradeCategoryLabel(trade),
        metricLabel: formatAmountMetric(trade),
        markerKey: fmpCongressTradeMarkerKey(transactionDate, trade, stackIndex),
        tradeIdentityKey: congressTradeIdentityKey(trade),
        onChart: chartDates.has(transactionDate),
      });
    });
    if (out.length >= maxResults) break;
  }

  out.sort((a, b) => {
    const dateCmp = b.transactionDate.localeCompare(a.transactionDate);
    if (dateCmp !== 0) return dateCmp;
    return a.memberName.localeCompare(b.memberName);
  });
  return out.slice(0, maxResults);
}
